/** The first year with four digits: an earlier year in a date filter is one still being typed. */
const FIRST_COMPLETE_YEAR = 1000;

/** The first and last instants of the days a date-range column filter names, where the reader is. */
export type DayRangeInstants = {
  /** The first instant of the first day, or undefined when the range has no start. */
  first?: Date;
  /** The last instant of the last day, or undefined when the range has no end. */
  last?: Date;
};

/**
 * The local day a date filter names, or undefined when it names none. MRT's date pickers hand over
 * whatever the app's date adapter makes (a moment), so anything with `toDate` is read through it.
 *
 * The picker's field hands over a date at each key typed in the year, so "2026" arrives first as
 * the years 2, 20 and 202. A year below 1000 is a year not yet typed in full, and names no day:
 * otherwise the table sends a request for each key.
 */
export function dayOf(value: unknown): Date | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const date = typeof (value as { toDate?: unknown }).toDate === "function"
    ? (value as { toDate: () => Date }).toDate()
    : new Date(value as string | number | Date);
  if (Number.isNaN(date.getTime()) || date.getFullYear() < FIRST_COMPLETE_YEAR) return undefined;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * The instants a `filterVariant: "date-range"` column filter covers, or undefined when it cannot be
 * sent because it ends before it starts.
 *
 * The reader picks days where they are, and the server stores instants, so the range runs from the
 * first instant of the first day to the last instant of the last day: an invitation sent late on
 * the last day is still in it.
 */
export function dayRangeInstantsOf(range: unknown): DayRangeInstants | undefined {
  const [firstDay, lastDay] = Array.isArray(range) ? range : [];
  const first = dayOf(firstDay);
  const lastDayStart = dayOf(lastDay);
  const last = lastDayStart
    ? new Date(lastDayStart.getFullYear(), lastDayStart.getMonth(), lastDayStart.getDate() + 1, 0, 0, 0, -1)
    : undefined;

  if (first && last && first > last) return undefined;
  return { first, last };
}
